import { PaginatedTanstackQueryParams, RetrieveByUuidParams } from '@/lib/types/v1'
import { ref, toValue, watchEffect } from 'vue'
import { usePaginatedTanstackQueryParams } from '@/composables/helpers'
import { useQuery, UseQueryReturnType } from '@tanstack/vue-query'
import { httpClientWithoutInterceptors } from '@/utils/httpClient'
import { isArray, isBoolean, isNumber, isString, parseAmpResponse } from '@/lib/utils'
import { queryKeys } from '@/lib/query-keys/v1'
import {
    paginatedTicketsResponseSchema,
    RetrieveTicketResponse,
    retrieveTicketResponseSchema,
} from '@/lib/schema/v2/ticket'
import { AppError } from '@/lib/schema/v2/error'

export const usePaginatedTicketsQuery = (
    params?: PaginatedTanstackQueryParams | (() => PaginatedTanstackQueryParams)
) => {
    const enabled = ref(true)
    const staleTime = ref(0)
    const { page, limit, sort, filter, q, view, groupBy, fields, queryKey } =
        usePaginatedTanstackQueryParams(params)
    const keys = ref(queryKeys.tickets.key({ key: queryKey }))

    watchEffect(() => {
        const value: PaginatedTanstackQueryParams | undefined = toValue(params)

        if (isBoolean(value?.enabled)) {
            enabled.value = value.enabled
        }

        if (isNumber(value?.staleTime)) {
            staleTime.value = value.staleTime
        }

        if (isArray(value?.queryKeys)) {
            keys.value = value.queryKeys
        }
    })

    return useQuery({
        queryKey: keys,
        queryFn: async () => {
            const { data } = await httpClientWithoutInterceptors.get('v1/tickets', {
                params: {
                    page: page.value,
                    limit: limit.value,
                    sort: sort.value,
                    filter: filter.value,
                    q: q.value,
                    view: view.value,
                    group_by: groupBy.value,
                    fields: fields.value,
                },
            })

            return parseAmpResponse(data, paginatedTicketsResponseSchema)
        },
        select: (data) => {
            return parseAmpResponse(data, paginatedTicketsResponseSchema)
        },
        enabled: enabled,
        staleTime: staleTime,
    })
}

export const useRetrieveTicketByUuidQuery = (
    params: RetrieveByUuidParams | (() => RetrieveByUuidParams)
): UseQueryReturnType<RetrieveTicketResponse, AppError> => {
    const staleTime = ref(0)
    const enabled = ref(true)
    const refetchOnWindowFocus = ref(true)
    const keys = ref(queryKeys.tickets.item())
    const uuid = ref(null)
    const fields = ref<string | null>(null)

    watchEffect(() => {
        const value: RetrieveByUuidParams | undefined = toValue(params)

        if (!value.uuid) {
            enabled.value = false
        }

        uuid.value = value.uuid
        keys.value = queryKeys.tickets.item(uuid.value)

        if (isNumber(value?.staleTime)) {
            staleTime.value = value.staleTime
        }

        if (isBoolean(value?.enabled)) {
            enabled.value = value.enabled
        }

        if (isBoolean(value?.refetchOnWindowFocus)) {
            refetchOnWindowFocus.value = value.refetchOnWindowFocus
        }

        if (isArray(value?.queryKeys)) {
            keys.value = value.queryKeys
        }

        if (isString(value?.fields)) {
            fields.value = value.fields
        }
    })

    return useQuery({
        queryKey: keys,
        queryFn: async () => {
            const { data } = await httpClientWithoutInterceptors.get(`v1/tickets/${uuid.value}`, {
                params: {
                    fields: fields.value,
                },
            })

            return parseAmpResponse(data, retrieveTicketResponseSchema)
        },
        select: (data) => {
            return parseAmpResponse(data, retrieveTicketResponseSchema)
        },
        enabled: enabled,
        staleTime: staleTime,
        refetchOnWindowFocus: refetchOnWindowFocus,
    })
}
